import styles from "./MypageInput.module.scss";
import { useEffect, useState } from "react";
import { validateRequired, validateMaxlength } from "../../utils/validate";
import { calcEndOfYearAge } from "../../utils/calcAge";

const MypageBirthdayTextField = ({
  name = "birthday",
  label = "生年月日",
  age_label = "年齢(年度末時点)",
  maxLength = 10,
  register,
  setValue = () => {},
  validation = {},
  errors,
  required = false,
  watch,
  isStatic = false,
  readOnly = false,
}) => {
  // 年齢
  const [age, setAge] = useState("--歳");
  const [selectedYear, setSelectedYear] = useState("");
  const [selectedMonth, setSelectedMonth] = useState("");
  const [selectedDay, setSelectedDay] = useState("");
  const birthdayValue = watch ? watch(name) : "";

  const padZero = (value) => String(value).padStart(2, "0");

  const buildDateString = (year, month, day) => {
    if (!year || !month || !day) return "";
    return `${year}-${padZero(month)}-${padZero(day)}`;
  };

  const updateAge = (dateValue) => {
    if (!dateValue) {
      setAge("--歳");
      return;
    }

    const eofy_age = calcEndOfYearAge(dateValue);
    setAge(String(eofy_age) + "歳");
  };

  useEffect(() => {
    if (!watch || typeof watch !== "function") return;
    if (!birthdayValue) {
      setSelectedYear("");
      setSelectedMonth("");
      setSelectedDay("");
      setAge("--歳");
      return;
    }

    const [year, month, day] = String(birthdayValue).split("-");
    if (!year || !month || !day) {
      setAge("--歳");
      return;
    }

    setSelectedYear(year);
    setSelectedMonth(String(Number(month)));
    setSelectedDay(String(Number(day)));
    updateAge(birthdayValue);
  }, [watch, name, birthdayValue]);

  const handleDateSelect = (part, value) => {
    const nextYear = part === "year" ? value : selectedYear;
    const nextMonth = part === "month" ? value : selectedMonth;
    const nextDay = part === "day" ? value : selectedDay;

    setSelectedYear(nextYear);
    setSelectedMonth(nextMonth);
    setSelectedDay(nextDay);

    const dateValue = buildDateString(nextYear, nextMonth, nextDay);
    setValue(name, dateValue || "");
    updateAge(dateValue);
  };

  const yearOptions = () => {
    const years = [];
    const currentYear = new Date().getFullYear();
    // 55歳から120歳までの年度末年齢範囲を生成
    for (let year = currentYear - 120; year <= currentYear - 54; year++) {
      years.push(year);
    }
    return years.reverse();
  };

  const monthOptions = () => {
    return Array.from({ length: 12 }, (_, index) => index + 1);
  };

  const dayOptions = () => {
    const year = Number(selectedYear || 0);
    const month = Number(selectedMonth || 0);

    if (!year || !month) {
      return Array.from({ length: 31 }, (_, index) => index + 1);
    }

    const lastDay = new Date(year, month, 0).getDate();
    return Array.from({ length: lastDay }, (_, index) => index + 1);
  };

  const returnDateText = () => {
    // 入力欄の値取得
    const birthday_value = watch(name);
    if (!birthday_value) return <p>&emsp;</p>;
    const birthday = new Date(birthday_value);
    let y = birthday.getFullYear();
    let m = birthday.getMonth() + 1;
    let d = birthday.getDate();
    return y + "年" + m + "月" + d + "日";
  };

  /**** バリデーションルール設定 ****/
  // 項目ごとのルール
  let txt_validation = validation;
  if (maxLength) {
    // 最大文字数が設定されていたら最大文字数バリデーションルール設定
    txt_validation = { ...txt_validation, ...validateMaxlength(maxLength) };
  }
  if (required) {
    // 必須項目なら必須バリデーションルール設定
    txt_validation = { ...txt_validation, ...validateRequired() };
  }

  return (
    <>
      <div className={styles.mypageform__block}>
        <div className={styles.mypageform__ttl}>
          {label}
          <span className={styles.mypageform__ttl__required}>
            {required && !isStatic ? "必須" : ""}
          </span>
        </div>
        {/* 日付入力欄 */}
        <div
          className={`${styles.mypageform__field} ${isStatic ? "d-none" : ""}`}
        >
          <div className={`${styles.mypageform__multifield}`}>
            {/* 年セレクトボックス */}
            <select
              value={selectedYear}
              id="year"
              className={`${styles.mypageform__multitxt} form-select ${
                errors[name] && !isStatic ? "is-invalid" : ""
              }`}
              onChange={(e) => handleDateSelect("year", e.target.value)}
              disabled={readOnly}
            >
              {yearOptions().map((year) => (
                <option key={year} value={year}>
                  {year}
                </option>
              ))}
            </select>
            <label
              htmlFor="year"
              className={styles.mypageform__multiselectlabel}
            >
              年
            </label>
            {/* 月セレクトボックス */}
            <select
              value={selectedMonth}
              id="month"
              className={`${styles.mypageform__multitxt} form-select ${
                errors[name] && !isStatic ? "is-invalid" : ""
              }`}
              onChange={(e) => handleDateSelect("month", e.target.value)}
              disabled={readOnly}
            >
              {monthOptions().map((month) => (
                <option key={month} value={month}>
                  {month}
                </option>
              ))}
            </select>
            <label
              htmlFor="month"
              className={styles.mypageform__multiselectlabel}
            >
              月
            </label>
            {/* 日セレクトボックス */}
            <select
              value={selectedDay}
              id="day"
              className={`${styles.mypageform__multitxt} form-select ${
                errors[name] && !isStatic ? "is-invalid" : ""
              }`}
              onChange={(e) => handleDateSelect("day", e.target.value)}
              disabled={readOnly}
            >
              {dayOptions().map((day) => (
                <option key={day} value={day}>
                  {day}
                </option>
              ))}
            </select>
            <label
              htmlFor="day"
              className={styles.mypageform__multiselectlabel}
            >
              日
            </label>
          </div>
          <input
            type="hidden"
            name={name}
            {...register(name, txt_validation)}
            value={watch && watch(name) ? watch(name) : ""}
          />
          <>
            {errors[name] ? (
              <p className={`invalid-feedback ${styles.mypageform__msg}`}>
                {errors[name].message}
              </p>
            ) : (
              <></>
            )}
          </>
        </div>
        {/* 確認画面表示用 */}
        {isStatic && (
          <div className={`${styles.mypageform__label} ps-4`}>
            {returnDateText()}
          </div>
        )}
      </div>
      {/* 年齢表示欄 */}
      <div className={styles.mypageform__block}>
        <div className={styles.mypageform__ttl}>
          {age_label}
          {!isStatic && (
            <span className={`${styles.mypageform__ttl__note} ms-4`}>
              ※55歳以上の方がご入学いただけます。
            </span>
          )}
        </div>
        <div className={styles.mypageform__field}>
          <div className={`${styles.mypageform__label} ps-4`}>{age}</div>
        </div>
      </div>
    </>
  );
};

export default MypageBirthdayTextField;
